import { SafetyControlSurvey } from "src/api/generated/erp/db/types/tables/safetyControlSurvey.ts";
import { SafetyControlSurveyView } from "src/api/generated/erp/db/types/tables/safetyControlSurveyView.ts";
import {
	SafetyControlSurveyEditApi,
	SafetyControlSurveyEditApi_SurveyFormInitData,
} from "src/api/generated/erp/safetyControlSurvey/api/safetyControlSurveyEditApi.ts";
import { AsyncForm } from "src/components/common/forms/AsyncForm.tsx";
import { FormTextField } from "src/components/common/forms/fields/FormTextField.tsx";
import { FormSelectField } from "src/components/common/forms/fields/FormSelectField.tsx";
import i18n from "i18next";
import { requireRule } from "src/components/common/forms/validation.ts";
import { FormCommonProps } from "src/components/common/forms/types.ts";
import { useGlobalInitData } from "src/contexts/GlobalInitDataContext.ts";
import { FormAsyncUserSelectField } from "src/components/views/users/FormAsyncUserSelectField.tsx";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";

export interface SafetyControlSurveyFormProps {
	survey: SafetyControlSurveyView | undefined;
	onCompleted: FormCommonProps<number>["onCompleted"];
	onFormEdited: () => void;
}

interface FormValues extends SafetyControlSurvey {}

export const SafetyControlSurveyForm = ({ survey, onCompleted, onFormEdited }: SafetyControlSurveyFormProps) => {
	const isNew = survey == null;
	const { defaultSiteId, userSites, appUserId } = useGlobalInitData();

	return (
		<AsyncForm<SafetyControlSurveyEditApi_SurveyFormInitData, FormValues, number>
			fetch={() => SafetyControlSurveyEditApi.getSurveyFormInitData()}
			getDefaultValues={(initData) =>
				survey ?? {
					safetyControlType: initData.surveyTypeOptions[0]?.value ?? "TR",
					siteId: defaultSiteId,
					teamId: initData.teamOptions[0]?.safetyControlSurveyTeamId ?? null,
					responsiblePersonId: appUserId,
				}
			}
			submit={async (values) => {
				if (isNew) {
					const result = await SafetyControlSurveyEditApi.insert({ survey: values });
					return result.safetyControlSurveyId;
				} else {
					const result = await SafetyControlSurveyEditApi.update({ survey: values });
					return result.safetyControlSurveyId;
				}
			}}
			onCompleted={onCompleted}
			onFormEdited={onFormEdited}
			columns={1}
			agentContext={{
				key: defineAgentUiContextKey("erp.safetyControl.survey.form"),
				data: {
					safetyControlSurveyId: survey?.safetyControlSurveyId ?? null,
				},
			}}
			render={({ control, data: initData }) => (
				<>
					<FormSelectField
						control={control}
						name="safetyControlType"
						label={i18n.t("type")}
						options={initData.surveyTypeOptions}
						getOptionKey={(o) => o.value}
						getOptionLabel={(o) => o.label}
						rules={requireRule()}
						disabled={!isNew}
						disableClearable
					/>
					<FormTextField control={control} name="name" label={i18n.t("name")} rules={requireRule()} />
					<FormTextField
						control={control}
						name="description"
						label={i18n.t("description")}
						multiline
						minRows={2}
					/>
					{userSites.length > 1 && (
						<FormSelectField
							control={control}
							name="siteId"
							label={i18n.t("site")}
							options={userSites}
							getOptionKey={(o) => o.siteId}
							getOptionLabel={(o) => o.siteName}
						/>
					)}
					<FormSelectField
						control={control}
						name="siteId"
						label={i18n.t("site")}
						options={userSites}
						getOptionKey={(o) => o.siteId}
						getOptionLabel={(o) => o.siteName}
					/>
					<FormSelectField
						control={control}
						name="teamId"
						label={i18n.t("team")}
						options={initData.teamOptions}
						getOptionKey={(o) => o.safetyControlSurveyTeamId}
						getOptionLabel={(o) => o.name}
					/>
					<FormAsyncUserSelectField
						control={control}
						name="responsiblePersonId"
						label={i18n.t("responsible_person")}
					/>
					<FormTextField control={control} name="participants" label={i18n.t("participants")} multiline />
				</>
			)}
		/>
	);
};
